/**
 * P&L category resolution.
 *
 * Shape of the data model:
 * - inventory_items.category  — operational subcategory (what the UI labels
 *                               "Subcategory"). Source of truth for item ops.
 * - pnl_categories            — per-store named P&L buckets (Food, Wine, …).
 * - pnl_category_mappings     — links subcategory text values to a P&L bucket.
 *                               Same subcategory may appear at most once per
 *                               store. A subcategory with no row is "Unmapped".
 *
 * Resolution is lazy + cached per store with a short TTL so views can call
 * getPLCategoryMap(storeId) freely. Call invalidatePLCategoryCache(storeId)
 * from any code path that mutates pnl_categories / pnl_category_mappings.
 */

export const UNMAPPED_LABEL = 'Unmapped';

const CACHE_TTL_MS = 60_000;
const _cache = new Map(); // storeId -> { map, byId, loadedAt }
const _inflight = new Map(); // storeId -> Promise

const normalizeKey = (s) => (s || '').trim().toLowerCase();

async function fetchPLCategoryMap(supabase, storeId) {
  const [catRes, mapRes] = await Promise.all([
    supabase
      .from('pnl_categories')
      .select('id, name, sort_order')
      .eq('store_id', storeId)
      .order('sort_order', { ascending: true }),
    supabase
      .from('pnl_category_mappings')
      .select('pnl_category_id, inventory_category')
      .eq('store_id', storeId),
  ]);

  if (catRes.error) throw catRes.error;
  if (mapRes.error) throw mapRes.error;

  const byId = new Map();
  (catRes.data || []).forEach((c) => byId.set(c.id, c.name));

  const map = new Map();
  (mapRes.data || []).forEach((row) => {
    const name = byId.get(row.pnl_category_id);
    if (!name) return;
    map.set(normalizeKey(row.inventory_category), name);
  });

  return { map, byId, loadedAt: Date.now() };
}

/**
 * Load (or reuse cached) P&L mapping for a store. Deduplicates concurrent
 * callers via an in-flight promise map.
 */
export async function getPLCategoryMap(supabase, storeId) {
  if (!supabase || !storeId) return { map: new Map(), byId: new Map(), loadedAt: 0 };
  const sid = Number(storeId);

  const cached = _cache.get(sid);
  if (cached && Date.now() - cached.loadedAt < CACHE_TTL_MS) return cached;

  const existing = _inflight.get(sid);
  if (existing) return existing;

  const promise = fetchPLCategoryMap(supabase, sid)
    .then((entry) => {
      _cache.set(sid, entry);
      return entry;
    })
    .finally(() => {
      _inflight.delete(sid);
    });
  _inflight.set(sid, promise);
  return promise;
}

/** Synchronous resolver against an already-loaded map. */
export function resolvePLCategoryFromMap(subcategory, mapEntry) {
  const key = normalizeKey(subcategory);
  if (!key) return UNMAPPED_LABEL;
  const m = mapEntry?.map;
  if (!m || m.size === 0) return UNMAPPED_LABEL;
  return m.get(key) || UNMAPPED_LABEL;
}

/**
 * Report-time resolver that prefers the frozen pl_category_snapshot (if the
 * row was snapshotted at session close) and falls back to a live lookup.
 * Call with the count_items row and the loaded map entry.
 */
export function plCategoryForCountRow(countItemRow, mapEntry) {
  if (!countItemRow) return UNMAPPED_LABEL;
  const frozen = countItemRow.pl_category_snapshot;
  if (frozen && String(frozen).trim().length > 0) return frozen;
  const sub =
    countItemRow.category_snapshot ||
    countItemRow.item_snapshot?.category ||
    countItemRow.category ||
    '';
  return resolvePLCategoryFromMap(sub, mapEntry);
}

/** Invalidate one store (e.g. after a mapping edit) or the whole cache. */
export function invalidatePLCategoryCache(storeId) {
  if (storeId == null) {
    _cache.clear();
    _inflight.clear();
    return;
  }
  const sid = Number(storeId);
  _cache.delete(sid);
  _inflight.delete(sid);
}

/**
 * Compute the set of unmapped subcategories for a store (used by the
 * Stage 5 dashboard nudge). Accepts a list of subcategory strings that
 * the store actually uses (typically the distinct set from inventory_items).
 */
export function findUnmappedSubcategories(subcategories, mapEntry) {
  const unique = [...new Set((subcategories || []).map((s) => (s || '').trim()).filter(Boolean))];
  if (!mapEntry || !mapEntry.map || mapEntry.map.size === 0) return unique;
  return unique.filter((s) => !mapEntry.map.has(normalizeKey(s)));
}

